import { Controller } from "@hotwired/stimulus"
import Cropper from "cropperjs"

// Target aspect ratios per image cell type (mirrors BlogCell.image_types).
const IMAGE_RATIOS = {
  photo_landscape: 700 / 490,
  photo_portrait: 490 / 700,
  panorama: 1100 / 300
}

// Owns the cell-edit modal: switches panels by cell type and runs Cropper.js
// for photo cells. Crop is reported as fractions (0..1) of the source image so
// it survives the server-side downscaling of the stored original.
export default class extends Controller {
  static targets = [
    "panel", "image", "file", "placeholder",
    "cropX", "cropY", "cropW", "cropH"
  ]
  static values = { type: String, src: String }

  connect() {
    this.showPanelFor(this.typeValue)
    if (this.isImageType(this.typeValue) && this.hasSrc()) {
      this.initCropper(IMAGE_RATIOS[this.typeValue])
    }
  }

  disconnect() {
    this.destroyCropper()
  }

  typeChanged(event) {
    this.typeValue = event.target.value
    this.showPanelFor(this.typeValue)
    if (!this.isImageType(this.typeValue)) return

    const ratio = IMAGE_RATIOS[this.typeValue]
    if (this.cropper) {
      this.cropper.setAspectRatio(ratio)
    } else if (this.hasSrc()) {
      this.initCropper(ratio)
    }
  }

  fileSelected(event) {
    const file = event.target.files[0]
    if (!file) return

    const url = URL.createObjectURL(file)
    this.imageTarget.src = url
    this.imageTarget.classList.remove("hidden")
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add("hidden")
    // A new image invalidates any previous crop.
    this.clearCropFields()

    const ratio = IMAGE_RATIOS[this.typeValue] || 1
    if (this.cropper) {
      this.cropper.replace(url)
      this.cropper.setAspectRatio(ratio)
    } else {
      this.initCropper(ratio)
    }
  }

  // Runs on form submit: persist the crop box as fractions of the natural image.
  sync() {
    if (!this.cropper || !this.isImageType(this.typeValue)) return

    const data = this.cropper.getData(true)
    const { naturalWidth: w, naturalHeight: h } = this.cropper.getImageData()
    if (!w || !h) return

    const clamp = (v) => Math.min(1, Math.max(0, v))
    this.cropXTarget.value = clamp(data.x / w)
    this.cropYTarget.value = clamp(data.y / h)
    this.cropWTarget.value = clamp(data.width / w)
    this.cropHTarget.value = clamp(data.height / h)
  }

  // --- helpers ---

  initCropper(ratio) {
    this.imageTarget.classList.remove("hidden")
    // Defer one frame so the <dialog> is open and the image has layout size.
    requestAnimationFrame(() => {
      if (this.cropper) return
      this.cropper = new Cropper(this.imageTarget, {
        aspectRatio: ratio,
        viewMode: 1,
        autoCropArea: 1,
        background: false,
        ready: () => this.applyExistingCrop()
      })
    })
  }

  applyExistingCrop() {
    const fx = parseFloat(this.cropXTarget.value)
    const fy = parseFloat(this.cropYTarget.value)
    const fw = parseFloat(this.cropWTarget.value)
    const fh = parseFloat(this.cropHTarget.value)
    if ([fx, fy, fw, fh].some((n) => Number.isNaN(n))) return

    const { naturalWidth: w, naturalHeight: h } = this.cropper.getImageData()
    this.cropper.setData({
      x: fx * w, y: fy * h, width: fw * w, height: fh * h
    })
  }

  destroyCropper() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
  }

  showPanelFor(type) {
    const wanted = this.isImageType(type) ? "photo" : type
    this.panelTargets.forEach((el) => {
      el.hidden = el.dataset.panel !== wanted
    })
  }

  clearCropFields() {
    [this.cropXTarget, this.cropYTarget, this.cropWTarget, this.cropHTarget]
      .forEach((el) => { el.value = "" })
  }

  isImageType(type) {
    return Object.prototype.hasOwnProperty.call(IMAGE_RATIOS, type)
  }

  hasSrc() {
    return this.srcValue && this.srcValue.length > 0
  }
}
